import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '../hooks/useToast';
import { useAuthStore } from '../store/authStore';
import { getErrorMessage } from '../lib/api';
import { authService } from '../services/auth.service';
import { LogIn } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Reasons the API (or /auth/callback) sends back as ?sso_error= after a
// Microsoft sign-in that didn't work (ENG-746).
const SSO_ERRORS: Record<string, string> = {
  no_account: "There's no Axis360 user for your Microsoft account. Ask an Axis360 admin to add you.",
  inactive: 'Your Axis360 user is turned off. Ask an Axis360 admin to turn it back on.',
  expired: 'Microsoft sign-in took too long. Please try again.',
  failed: "Microsoft sign-in didn't finish. Please try again.",
};

export const Login = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await login(data);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <main className="max-w-md w-full space-y-8">
        <header>
          <div className="flex justify-center">
            <div className="bg-primary-600 text-white rounded-full p-3">
              <LogIn className="h-8 w-8" />
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <img src="/axis360.svg" alt="Axis360" className="h-12" />
          </div>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your account
          </p>
        </header>

        {ssoError && (
          <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {SSO_ERRORS[ssoError] ?? SSO_ERRORS.failed}
          </div>
        )}

        <div className="mt-8 space-y-6">
          <a href={authService.microsoftSignInUrl()} className="w-full btn btn-primary flex items-center justify-center gap-2">
            <MicrosoftLogo />
            Sign in with Microsoft
          </a>
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-50 px-2 text-gray-500">or use email and password</span>
            </div>
          </div>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                {...register('email')}
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="input"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                {...register('password')}
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="input"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-secondary"
            >
              {isLoading ? 'Signing in...' : 'Sign in with email'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

// Microsoft's four-square mark, per their sign-in button guidelines.
const MicrosoftLogo = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default Login;
